import type React from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
	ClearFiltersContext,
	type ClearFiltersContextType,
	NO_ACTIONS,
	NO_TAB_COUNTS,
	PageContext,
	type PageContextType,
	type TabCounts,
} from './context'
import type { BackButtonConfig, OverflowTabsConfig, PageAction, PageTabsConfig } from './types'

export function PageProvider({ children }: { children: React.ReactNode }) {
	const [actions, setActions] = useState<PageAction[]>(NO_ACTIONS)
	const [actionsPathname, setActionsPathname] = useState<string | null>(null)
	const [tabCounts, setTabCounts] = useState<TabCounts>(NO_TAB_COUNTS)
	const [dynamicTabs, setPageTabs] = useState<PageTabsConfig | null>(null)
	const [backButton, setBackButton] = useState<BackButtonConfig | null>(null)
	const [hideOverflowTabs, setHideOverflowTabs] = useState<boolean>(false)
	const [overflowTabs, setOverflowTabs] = useState<OverflowTabsConfig | null>(null)
	// A ref, not state: registering a search bar's clear or focus function must not re-render anything.
	const clearFiltersRef = useRef<(() => void) | null>(null)
	const focusSearchRef = useRef<(() => void) | null>(null)

	// Never re-identifies, so a search bar consuming it renders once and then only for its own props.
	const clearFilters = useMemo<ClearFiltersContextType>(
		() => ({
			registerClearFilters: fn => {
				clearFiltersRef.current = fn
			},
			clearRegularFilters: () => clearFiltersRef.current?.(),
			registerFocusSearch: fn => {
				focusSearchRef.current = fn
			},
			hasMainSearch: () => focusSearchRef.current !== null,
			focusMainSearch: () => {
				focusSearchRef.current?.()
			},
		}),
		[],
	)
	// Set straight from a ref callback in PageHeader; useState setters are stable, so the ref never churns.
	const [headerControlsSlot, setHeaderControlsSlot] = useState<HTMLElement | null>(null)
	const [toolbarExtrasSlot, setToolbarExtrasSlot] = useState<HTMLElement | null>(null)
	const [isPageHeaderEmpty, setIsPageHeaderEmpty] = useState<boolean>(false)
	const [openToolbar, setOpenToolbar] = useState<(() => void) | null>(null)

	const setPageActions = useCallback((next: PageAction[], pathname: string | null) => {
		setActions(next)
		setActionsPathname(pathname)
	}, [])

	const updatePageAction = useCallback((actionId: string, updates: Partial<PageAction>) => {
		setActions(prev =>
			prev.map(action => {
				if (action.id !== actionId) {
					return action
				}
				// Safe: the caller only ever passes properties the original action type already has.
				return { ...action, ...updates } as PageAction
			}),
		)
	}, [])

	const clearPageActions = useCallback(() => {
		setActions(NO_ACTIONS)
		setActionsPathname(null)
	}, [])

	const clearPageTabs = useCallback(() => {
		setPageTabs(null)
	}, [])

	const clearBackButton = useCallback(() => {
		setBackButton(null)
	}, [])

	// Every setter here is stable, either a useState setter or a `useCallback(…, [])`, so this only
	// re-identifies when a value actually changes. Without it the provider hands every consumer a new
	// object on each of its own renders, and the whole header re-renders for state it never reads.
	const contextValue: PageContextType = useMemo(
		() => ({
			actions,
			actionsPathname,
			setPageActions,
			updatePageAction,
			clearPageActions,
			tabCounts,
			setTabCounts,
			dynamicTabs,
			setPageTabs,
			clearPageTabs,
			backButton,
			setBackButton,
			clearBackButton,
			overflowTabs,
			setOverflowTabs,
			hideOverflowTabs,
			setHideOverflowTabs,
			headerControlsSlot,
			setHeaderControlsSlot,
			toolbarExtrasSlot,
			setToolbarExtrasSlot,
			isPageHeaderEmpty,
			setIsPageHeaderEmpty,
			openToolbar,
			setOpenToolbar,
		}),
		[
			actions,
			actionsPathname,
			setPageActions,
			updatePageAction,
			clearPageActions,
			tabCounts,
			dynamicTabs,
			clearPageTabs,
			backButton,
			clearBackButton,
			overflowTabs,
			hideOverflowTabs,
			headerControlsSlot,
			toolbarExtrasSlot,
			isPageHeaderEmpty,
			openToolbar,
		],
	)

	return (
		<ClearFiltersContext.Provider value={clearFilters}>
			<PageContext.Provider value={contextValue}>{children}</PageContext.Provider>
		</ClearFiltersContext.Provider>
	)
}
